import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  cashLedgerApi,
  formatShekels,
  type CashIncome,
  type CashExpense,
  type CashTransaction,
  type FundBalance,
} from "../../api/cashLedgerApi";
import { toClientApiError } from "../../api/httpClient";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { useQuickCreate } from "../../next/quickCreate";
import { ListStatStrip } from "../../ui/ListStatStrip";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { AccountBalanceWalletOutlinedIcon } from "../../ui/appIcons";
import {
  ExpenseCreateDialog,
  IncomeCreateDialog,
  PAYMENT_METHOD_HE,
} from "./CashEntryDialogs";
import { CashFileImportDialog } from "./CashFileImportDialog";
import {
  KIND_HE,
  loadAcrossCustomers,
  type CashSectionBaseProps,
  type Scoped,
} from "./cashLedgerUtils";

type InnerTab = "osh" | "incomes" | "expenses";

const INCOME_STATUS_HE: Record<string, string> = {
  draft: "טיוטה",
  completed: "הושלם",
  partial: "חלקי",
  cancelled: "בוטל",
};

const EXPENSE_STATUS_HE: Record<string, string> = {
  recorded: "נרשם",
  cancelled: "בוטל",
};

/** quick create (top bar "חדש" → הכנסה / הוצאה) opens the section's own dialog */
type NewSignalProps = CashSectionBaseProps & { openNewSignal?: boolean; onOpenedNew?: () => void };

export function LedgerSections(props: CashSectionBaseProps) {
  const [tab, setTab] = useState<InnerTab>("osh");
  const [pendingNew, setPendingNew] = useState<InnerTab | null>(null);
  useQuickCreate("income", props.archived ? null : () => {
    setTab("incomes");
    setPendingNew("incomes");
  });
  useQuickCreate("expense", props.archived ? null : () => {
    setTab("expenses");
    setPendingNew("expenses");
  });
  const tabs: { key: InnerTab; label: string }[] = [
    { key: "osh", label: "עו״ש" },
    { key: "incomes", label: "הכנסות" },
    { key: "expenses", label: "הוצאות" },
  ];

  return (
    <div>
      <nav className="cash-ledger-panel__tabs tb-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "tb-tabs__tab is-active" : "tb-tabs__tab"}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "osh" ? <OshSection {...props} /> : null}
      {tab === "incomes" ? (
        <IncomesSection {...props} openNewSignal={pendingNew === "incomes"} onOpenedNew={() => setPendingNew(null)} />
      ) : null}
      {tab === "expenses" ? (
        <ExpensesSection {...props} openNewSignal={pendingNew === "expenses"} onOpenedNew={() => setPendingNew(null)} />
      ) : null}
    </div>
  );
}

function useScope(props: CashSectionBaseProps) {
  const showAll = props.customerId === null;
  const scopeIds = useMemo(
    () => (props.customerId ? [props.customerId] : props.customerIds),
    [props.customerId, props.customerIds],
  );
  const writeTargetId = props.customerId ?? props.writeCustomerId;
  const customerLabel = (id: string) => props.customerNames[id] ?? "—";
  return { showAll, scopeIds, writeTargetId, customerLabel };
}

function OshSection(props: CashSectionBaseProps) {
  const { showAll, scopeIds, customerLabel } = useScope(props);
  const [transactions, setTransactions] = useState<Scoped<CashTransaction>[]>([]);
  const [fundBalances, setFundBalances] = useState<Scoped<FundBalance>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const [tx, fb] = await Promise.all([
        loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listTransactions(id)),
        loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listFundBalances(id)),
      ]);
      setTransactions(tx);
      setFundBalances(fb);
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalAgorot = fundBalances.reduce((sum, b) => sum + b.amountAgorot, 0);

  return (
    <div>
      <ListPageHeader onRefresh={() => void load()} />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <ListStatStrip
        title="יתרות קרן"
        columns={2}
        activeKey={null}
        onSelect={() => undefined}
        cards={[
          {
            key: null,
            label: "סה״כ יתרות (₪)",
            value: Math.round(totalAgorot) / 100,
            color: "#0f766e",
            icon: <AccountBalanceWalletOutlinedIcon />,
          },
          {
            key: "funds",
            label: "מספר קרנות",
            value: fundBalances.length,
            color: "#1976d2",
            icon: <AccountBalanceWalletOutlinedIcon />,
          },
        ]}
      />
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "occurredAt", title: "תאריך", sortable: true, minWidth: 120 },
            { key: "kind", title: "סוג", sortable: true, minWidth: 100 },
            { key: "amount", title: "סכום", sortable: true, minWidth: 100 },
            { key: "note", title: "הערה", minWidth: 160 },
          ]}
          data={transactions.map((row) => ({
            id: `${row.scopeCustomerId}:${row.id}`,
            customer: customerLabel(row.scopeCustomerId),
            occurredAt: new Date(row.occurredAt).toLocaleDateString("he-IL"),
            kind: KIND_HE[row.kind] ?? row.kind,
            amount: formatShekels(row.amountAgorot),
            note: row.note ?? "—",
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין תנועות מזומן עדיין."
          exportFileName="cash-transactions"
          showExport
          showFilters
          showColumnPicker
        />
      </div>
    </div>
  );
}

function IncomesSection(props: NewSignalProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<CashIncome>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { openNewSignal, onOpenedNew } = props;
  useEffect(() => {
    if (!openNewSignal) return;
    if (!props.archived) setDialogOpen(true);
    onOpenedNew?.();
  }, [openNewSignal, onOpenedNew, props.archived]);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listIncomes(id)));
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="הכנסה חדשה"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
        secondary={
          <SettingsButton
            type="button"
            variant="settings"
            size="sm"
            disabled={props.archived}
            onClick={() => setImportOpen(true)}
          >
            ייבוא מקובץ
          </SettingsButton>
        }
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "incomeDate", title: "תאריך", sortable: true, minWidth: 110 },
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 160 },
            { key: "amount", title: "סכום", sortable: true, minWidth: 100 },
            { key: "paid", title: "שולם", sortable: true, minWidth: 100 },
            { key: "paymentMethod", title: "אמצעי תשלום", sortable: true, minWidth: 120 },
            { key: "counterparty", title: "גורם", filterable: true, minWidth: 120 },
            { key: "reference", title: "אסמכתא", minWidth: 100 },
            { key: "status", title: "סטטוס", sortable: true, minWidth: 90 },
            { key: "receipt", title: "קבלה", minWidth: 100 },
            {
              key: "actions",
              title: "פעולות",
              minWidth: 180,
              render: (_v, row) => {
                const income = rows.find(
                  (r) => `${r.scopeCustomerId}:${r.id}` === String(row.id),
                );
                if (!income || props.archived || income.status === "cancelled") {
                  return "—";
                }
                const canIssue =
                  income.receiptSource !== "ezcount" &&
                  income.receiptSource !== "nedarim";
                return (
                  <TableRowActions>
                    {canIssue ? (
                      <QuietButton
                        type="button"
                        onClick={() => {
                          void (async () => {
                            try {
                              await cashLedgerApi.issueIncomeReceipt(
                                income.scopeCustomerId,
                                income.id,
                              );
                              await load();
                            } catch (err) {
                              setError(toClientApiError(err).message);
                            }
                          })();
                        }}
                      >
                        הנפק קבלה
                      </QuietButton>
                    ) : null}
                    <QuietButton
                      type="button"
                      tone="danger"
                      onClick={() => {
                        void (async () => {
                          try {
                            await cashLedgerApi.cancelIncome(
                              income.scopeCustomerId,
                              income.id,
                            );
                            await load();
                          } catch (err) {
                            setError(toClientApiError(err).message);
                          }
                        })();
                      }}
                    >
                      בטל
                    </QuietButton>
                  </TableRowActions>
                );
              },
            },
          ]}
          data={rows.map((row) => ({
            id: `${row.scopeCustomerId}:${row.id}`,
            customer: customerLabel(row.scopeCustomerId),
            incomeDate: new Date(row.incomeDate).toLocaleDateString("he-IL"),
            title: row.title,
            amount: formatShekels(row.amountAgorot),
            paid: formatShekels(row.amountPaidAgorot),
            paymentMethod: row.paymentMethod
              ? (PAYMENT_METHOD_HE[row.paymentMethod] ?? row.paymentMethod)
              : "—",
            counterparty: row.counterpartyName ?? "—",
            reference: row.reference ?? "—",
            status: INCOME_STATUS_HE[row.status] ?? row.status,
            receipt:
              row.receiptSource === "ezcount"
                ? "איזיקאונט"
                : row.receiptSource === "nedarim"
                  ? "נדרים"
                  : row.receiptSource === "manual"
                    ? "ידני"
                    : "—",
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין הכנסות עדיין."
          exportFileName="cash-incomes"
          showExport
          showFilters
          showColumnPicker
        />
      </div>

      <IncomeCreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        customerId={writeTargetId}
        onCreated={() => void load()}
      />

      <CashFileImportDialog<
        Awaited<ReturnType<typeof cashLedgerApi.previewIncomeImportFile>>
      >
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="ייבוא הכנסות מקובץ"
        recommendImportType="incomes"
        hint="עמודות: כותרת/title, סכום/amount, תאריך/date (אופציונלי), הערות/notes"
        sampleColumns={[
          { key: "title", label: "כותרת" },
          { key: "amountAgorot", label: "סכום (אגורות)" },
          { key: "date", label: "תאריך" },
        ]}
        onPreview={(file) => cashLedgerApi.previewIncomeImportFile(writeTargetId, file)}
        onCommit={(preview) =>
          cashLedgerApi.commitIncomeImportFile(writeTargetId, preview.rows)
        }
        onDone={() => void load()}
      />
    </div>
  );
}

function ExpensesSection(props: NewSignalProps) {
  const { showAll, scopeIds, writeTargetId, customerLabel } = useScope(props);
  const [rows, setRows] = useState<Scoped<CashExpense>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { openNewSignal, onOpenedNew } = props;
  useEffect(() => {
    if (!openNewSignal) return;
    if (!props.archived) setDialogOpen(true);
    onOpenedNew?.();
  }, [openNewSignal, onOpenedNew, props.archived]);

  const load = useCallback(async () => {
    if (scopeIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setRows(
        await loadAcrossCustomers(scopeIds, (id) => cashLedgerApi.listCashExpenses(id)),
      );
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <ListPageHeader
        onRefresh={() => void load()}
        primaryLabel="הוצאה חדשה"
        onPrimary={() => {
          if (!props.archived) setDialogOpen(true);
        }}
        primaryDisabled={props.archived}
        secondary={
          <SettingsButton
            type="button"
            variant="settings"
            size="sm"
            disabled={props.archived}
            onClick={() => setImportOpen(true)}
          >
            ייבוא מקובץ
          </SettingsButton>
        }
      />
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}
      <div className="page-section__table-surface">
        <TableKit
          columns={[
            ...(showAll
              ? [{ key: "customer", title: "קופה", sortable: true, minWidth: 140 }]
              : []),
            { key: "incurredAt", title: "תאריך", sortable: true, minWidth: 110 },
            { key: "title", title: "כותרת", sortable: true, filterable: true, minWidth: 160 },
            { key: "amount", title: "סכום", sortable: true, minWidth: 100 },
            { key: "paymentMethod", title: "אמצעי תשלום", sortable: true, minWidth: 120 },
            { key: "status", title: "סטטוס", sortable: true, minWidth: 90 },
            {
              key: "actions",
              title: "פעולות",
              minWidth: 120,
              render: (_v, row) => {
                const expense = rows.find(
                  (r) => `${r.scopeCustomerId}:${r.id}` === String(row.id),
                );
                if (!expense || props.archived || expense.status === "cancelled") {
                  return "—";
                }
                return (
                  <TableRowActions>
                    <QuietButton
                      type="button"
                      tone="danger"
                      onClick={() => {
                        void (async () => {
                          try {
                            await cashLedgerApi.cancelCashExpense(
                              expense.scopeCustomerId,
                              expense.id,
                            );
                            await load();
                          } catch (err) {
                            setError(toClientApiError(err).message);
                          }
                        })();
                      }}
                    >
                      בטל
                    </QuietButton>
                  </TableRowActions>
                );
              },
            },
          ]}
          data={rows.map((row) => ({
            id: `${row.scopeCustomerId}:${row.id}`,
            customer: customerLabel(row.scopeCustomerId),
            incurredAt: new Date(row.incurredAt).toLocaleDateString("he-IL"),
            title: row.title,
            amount: formatShekels(row.amountAgorot),
            paymentMethod: row.paymentMethod
              ? (PAYMENT_METHOD_HE[row.paymentMethod] ?? row.paymentMethod)
              : "—",
            status: EXPENSE_STATUS_HE[row.status] ?? row.status,
          }))}
          loading={loading}
          clientSideMode
          emptyMessage="אין הוצאות מזומן עדיין."
          exportFileName="cash-expenses"
          showExport
          showFilters
          showColumnPicker
        />
      </div>

      <ExpenseCreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        customerId={writeTargetId}
        onCreated={() => void load()}
      />

      <CashFileImportDialog<
        Awaited<ReturnType<typeof cashLedgerApi.previewExpenseImportFile>>
      >
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="ייבוא הוצאות מקובץ"
        recommendImportType="expenses"
        hint="עמודות: כותרת/title, סכום/amount, תאריך/date (אופציונלי), הערות/notes"
        sampleColumns={[
          { key: "title", label: "כותרת" },
          { key: "amountAgorot", label: "סכום (אגורות)" },
          { key: "date", label: "תאריך" },
        ]}
        onPreview={(file) => cashLedgerApi.previewExpenseImportFile(writeTargetId, file)}
        onCommit={(preview) =>
          cashLedgerApi.commitExpenseImportFile(writeTargetId, preview.rows)
        }
        onDone={() => void load()}
      />
    </div>
  );
}
